<template>
  <div class="flex flex-col gap-3">
    <!-- WATERFALL MODE -->
    <template v-if="waterfallContext">
      <div v-if="waterfallLoading" class="text-sm text-gray-400 py-4 text-center">
        <font-awesome-icon :icon="['fas', 'circle-notch']" spin class="mr-1" />
        Searching provider directory...
      </div>

      <template v-else-if="waterfallResults">
        <div v-if="waterfallResults.context" class="text-xs bg-gray-50 border border-gray-200 rounded px-3 py-2 mb-2">
          <div v-if="contextProviderName" class="font-medium text-gray-800">
            <span class="sensitive">{{ contextProviderName }}</span> <span v-if="waterfallResults.context.npi" class="font-normal text-gray-400">NPI <span class="sensitive">{{ waterfallResults.context.npi }}</span></span>
          </div>
          <div v-else-if="waterfallResults.context.npi" class="font-medium text-gray-800">
            NPI <span class="sensitive">{{ waterfallResults.context.npi }}</span>
          </div>
          <div v-if="waterfallResults.context.organization" class="text-gray-500 sensitive">
            <span class="sensitive" data-spoof-kind="practice">{{ waterfallResults.context.organization }}</span> <span v-if="waterfallResults.context.practice_state"> · <span class="sensitive">{{ waterfallResults.context.practice_state }}</span></span>
          </div>
        </div>
        <template v-if="matchingAddressBookEntries.length">
          <div class="mb-3">
            <div class="flex items-center gap-2 mb-1">
              <span class="text-[10px] font-semibold uppercase tracking-wide rounded-full px-2 py-0.5 border bg-purple-100 text-purple-700 border-purple-200">Saved</span>
              <span class="text-xs text-gray-500">Previously saved to address book</span>
            </div>
            <ul class="divide-y divide-gray-100 border border-gray-100 rounded">
              <li v-for="entry in matchingAddressBookEntries" :key="entry.direct_address" class="p-2.5">
                <button
                  type="button"
                  class="w-full text-left hover:bg-gray-50 -m-2.5 p-2.5 rounded transition-colors"
                  @click="selectRecipient(entry)"
                >
                  <div class="text-xs">
                    <div class="font-medium text-gray-800">
                      <span class="sensitive">{{ displayName(entry) }}</span> <span v-if="entry.npi" class="font-normal text-gray-400">NPI <span class="sensitive">{{ entry.npi }}</span></span>
                    </div>
                    <div class="text-gray-500 truncate sensitive">{{ entry.direct_address }}</div>
                    <div v-if="entry.organization" class="text-gray-500 sensitive">{{ entry.organization }}</div>
                  </div>
                </button>
              </li>
            </ul>
          </div>
        </template>

        <p class="text-xs text-gray-500 mb-1">Addresses from the national DirectTrust directory.</p>

        <template v-for="tier in visibleTiers" :key="tier.tier">
          <div class="mb-3">
            <div class="flex items-center gap-2 mb-1">
              <span
                :class="tierBadgeClass(tier.tier)"
                class="text-[10px] font-semibold uppercase tracking-wide rounded-full px-2 py-0.5 border"
              >
                {{ tier.badge }}
              </span>
              <span class="text-xs text-gray-500">{{ tier.label }}</span>
            </div>
            <ul class="divide-y divide-gray-100 border border-gray-100 rounded">
              <li v-for="result in tier.results" :key="result.direct_address" class="p-2.5">
                <button
                  type="button"
                  class="w-full text-left hover:bg-gray-50 -m-2.5 p-2.5 rounded transition-colors"
                  @click="selectWaterfallResult(result)"
                >
                  <div class="text-xs">
                    <div class="font-medium text-gray-800">
                      <span class="sensitive">{{ displayName(result) }}</span> <span v-if="result.npi" class="font-normal text-gray-400">NPI <span class="sensitive">{{ result.npi }}</span></span>
                    </div>
                    <div class="text-gray-500 truncate sensitive">{{ result.direct_address }}</div>
                    <div v-if="result.organization" class="text-gray-500 sensitive">{{ result.organization }}</div>
                    <div v-if="result.flags && result.flags.length" class="flex gap-1 mt-1">
                      <span
                        v-for="flag in result.flags"
                        :key="flag.type"
                        :class="flagClass(flag.type)"
                        class="text-[10px] rounded-full px-1.5 py-0.5 border"
                      >
                        {{ flagShortLabel(flag.type) }}
                      </span>
                    </div>
                  </div>
                </button>
              </li>
            </ul>
          </div>
        </template>

        <p v-if="!visibleTiers.length" class="text-xs text-gray-500 text-center py-3">
          No Direct address found. The practice may not have a Direct address set up.
        </p>

        <div class="flex items-center gap-3 mt-1">
          <button type="button" class="btn-link text-xs" @click="showDirectorySearch = true">
            <font-awesome-icon :icon="['fas', 'magnifying-glass']" class="mr-1" />
            Search manually
          </button>
          <button type="button" class="btn-link text-xs" @click="showAddressBook = !showAddressBook">
            <font-awesome-icon :icon="['fas', 'address-book']" class="mr-1" />
            {{ showAddressBook ? 'Hide' : 'Show' }} address book
          </button>
        </div>

        <template v-if="showAddressBook">
          <div class="relative mt-2">
            <font-awesome-icon
              :icon="['fas', 'magnifying-glass']"
              class="absolute left-3.5 top-1/2 -translate-y-1/2 text-blue-500 text-base"
            />
            <input
              v-model="searchQuery" data-demo-search
              type="text"
              placeholder="Filter address book..."
              class="sensitive w-full pl-10 pr-3 py-2.5 border-2 border-blue-200 rounded-lg text-sm shadow-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30"
            />
          </div>
          <div v-if="loadingContacts" class="text-sm text-gray-400 py-2">Loading…</div>
          <template v-else-if="hasContacts">
            <section v-if="savedContacts.length">
              <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Saved Contacts</p>
              <ul class="divide-y divide-gray-100 border border-gray-200 rounded overflow-hidden">
                <li v-for="entry in savedContacts" :key="entry.direct_address">
                  <button type="button" class="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition-colors" @click="selectRecipient(entry)">
                    <div class="flex items-center gap-2">
                      <span class="font-medium text-gray-900 sensitive">{{ displayName(entry) }}</span>
                      <span v-if="entry.organization" class="text-gray-500 text-xs">(<span class="sensitive">{{ entry.organization }}</span>)</span>
                    </div>
                    <span v-if="participantDisplayName(entry)" class="sensitive text-gray-400 text-xs">{{ entry.direct_address }}</span>
                  </button>
                </li>
              </ul>
            </section>
            <section v-if="internalContacts.length" :class="savedContacts.length ? 'mt-3' : ''">
              <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Internal Users</p>
              <ul class="divide-y divide-gray-100 border border-gray-200 rounded overflow-hidden">
                <li v-for="entry in internalContacts" :key="entry.direct_address">
                  <button type="button" class="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition-colors" @click="selectRecipient(entry)">
                    <div class="flex items-center gap-2">
                      <span class="font-medium text-gray-900 sensitive">{{ displayName(entry) }}</span>
                      <span class="text-[10px] font-semibold uppercase tracking-wide text-blue-600 bg-blue-50 border border-blue-200 rounded px-1">Internal</span>
                      <span v-if="entry.organization" class="text-gray-500 text-xs">(<span class="sensitive">{{ entry.organization }}</span>)</span>
                    </div>
                    <span v-if="participantDisplayName(entry)" class="sensitive text-gray-400 text-xs">{{ entry.direct_address }}</span>
                  </button>
                </li>
              </ul>
            </section>
          </template>
        </template>
      </template>
    </template>

    <!-- NO WATERFALL CONTEXT -->
    <template v-else>
      <div class="mb-2">
        <button type="button" class="btn-secondary w-full text-left text-sm" @click="showDirectorySearch = true">
          <font-awesome-icon :icon="['fas', 'magnifying-glass']" class="mr-1.5" />
          Search provider directory
        </button>
      </div>

      <div class="relative">
        <font-awesome-icon
          :icon="['fas', 'magnifying-glass']"
          class="absolute left-3.5 top-1/2 -translate-y-1/2 text-blue-500 text-base"
        />
        <input
          ref="searchInput"
          v-model="searchQuery" data-demo-search
          type="text"
          placeholder="Start typing a name or address to search…"
          class="sensitive w-full pl-10 pr-3 py-2.5 border-2 border-blue-200 rounded-lg text-sm shadow-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30"
        />
      </div>

      <div>
        <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Address Book</p>
        <div v-if="loadingContacts" class="text-sm text-gray-400 py-2">Loading…</div>
        <template v-else-if="hasContacts">
          <section v-if="savedContacts.length">
            <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Saved Contacts</p>
            <ul class="divide-y divide-gray-100 border border-gray-200 rounded overflow-hidden">
              <li v-for="entry in savedContacts" :key="entry.direct_address">
                <button type="button" class="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition-colors" @click="selectRecipient(entry)">
                  <div class="flex items-center gap-2">
                    <span class="font-medium text-gray-900 sensitive">{{ displayName(entry) }}</span>
                    <span v-if="entry.organization" class="text-gray-500 text-xs">(<span class="sensitive">{{ entry.organization }}</span>)</span>
                  </div>
                  <span v-if="participantDisplayName(entry)" class="sensitive text-gray-400 text-xs">{{ entry.direct_address }}</span>
                </button>
              </li>
            </ul>
            <p v-if="hasMoreSavedContacts" class="text-xs text-gray-500 mt-1.5">
              Showing the first {{ savedContactsLimit }} saved-contact matches — keep typing above to narrow results.
            </p>
          </section>

          <section v-if="internalContacts.length" :class="savedContacts.length ? 'mt-3' : ''">
            <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Internal Users</p>
            <ul class="divide-y divide-gray-100 border border-gray-200 rounded overflow-hidden">
              <li v-for="entry in internalContacts" :key="entry.direct_address">
                <button type="button" class="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition-colors" @click="selectRecipient(entry)">
                  <div class="flex items-center gap-2">
                    <span class="font-medium text-gray-900 sensitive">{{ displayName(entry) }}</span>
                    <span class="text-[10px] font-semibold uppercase tracking-wide text-blue-600 bg-blue-50 border border-blue-200 rounded px-1">Internal</span>
                    <span v-if="entry.organization" class="text-gray-500 text-xs">(<span class="sensitive">{{ entry.organization }}</span>)</span>
                  </div>
                  <span v-if="participantDisplayName(entry)" class="sensitive text-gray-400 text-xs">{{ entry.direct_address }}</span>
                </button>
              </li>
            </ul>
            <p v-if="hasMoreInternalContacts" class="text-xs text-gray-500 mt-1.5">
              Showing the first {{ internalContactsLimit }} team matches — keep typing above to narrow results.
            </p>
          </section>
        </template>
        <p v-else-if="!contactsUnavailable" class="text-sm text-gray-400 py-1">No matches in your address book.</p>
      </div>
    </template>

    <ProviderDirectorySearchModal
      v-model="showDirectorySearch"
      selectable
      :initial-npi="initialNpi"
      @select="selectFromDirectory"
      @address-book-updated="loadContacts"
    />
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import * as messagingApi from '@/services/api/providerMessaging.js';
import ProviderDirectorySearchModal from '@/components/shared/ProviderDirectorySearchModal.vue';
import { participantDisplayName } from '@/utils/providerMessaging.js';
import demoProtectedFields from '@/mixins/demoProtectedFields';

const INTERNAL_CONTACTS_LIMIT = 25;
const SAVED_CONTACTS_LIMIT = 20;

export default {
  name: 'RecipientPicker',
  components: { FontAwesomeIcon, ProviderDirectorySearchModal },
  mixins: [demoProtectedFields],
  // The address-book / directory search (provider names, organizations, Direct addresses) identifies real providers and practices. Demo Mode empties it and the tagged
  // input is read-only while protected. It is not persisted.
  demoProtectedFields: ['searchQuery'],

  props: {
    initialNpi: { type: String, default: '' },
    waterfallContext: { type: Object, default: null },
  },

  emits: ['select'],

  data() {
    return {
      contacts: [],
      searchQuery: '',
      loadingContacts: true,
      contactsUnavailable: false,
      showDirectorySearch: false,
      internalContactsLimit: INTERNAL_CONTACTS_LIMIT,
      savedContactsLimit: SAVED_CONTACTS_LIMIT,
      waterfallResults: null,
      waterfallLoading: false,
      showAddressBook: false,
    };
  },

  computed: {
    filteredContacts() {
      const q = this.searchQuery.toLowerCase();
      return this.contacts.filter(
        (c) =>
          !q ||
          this.displayName(c).toLowerCase().includes(q) ||
          (c.organization || '').toLowerCase().includes(q) ||
          (c.direct_address || '').toLowerCase().includes(q),
      );
    },

    internalContacts() {
      return this.matchingInternalContacts.slice(0, INTERNAL_CONTACTS_LIMIT);
    },

    savedContacts() {
      return this.matchingSavedContacts.slice(0, SAVED_CONTACTS_LIMIT);
    },

    matchingInternalContacts() {
      return this.filteredContacts.filter((contact) => contact.source === 'internal');
    },

    matchingSavedContacts() {
      return this.filteredContacts.filter((contact) => contact.source === 'contact');
    },

    hasMoreInternalContacts() {
      return this.matchingInternalContacts.length > INTERNAL_CONTACTS_LIMIT;
    },

    hasMoreSavedContacts() {
      return this.matchingSavedContacts.length > SAVED_CONTACTS_LIMIT;
    },

    hasContacts() {
      return this.internalContacts.length || this.savedContacts.length;
    },

    visibleTiers() {
      if (!this.waterfallResults?.tiers) return [];
      return this.waterfallResults.tiers.filter((t) => t.results.length > 0);
    },

    matchingAddressBookEntries() {
      if (!this.waterfallResults?.context || !this.contacts.length) return [];
      const ctx = this.waterfallResults.context;
      const npi = ctx.npi;
      const name = [ctx.first_name, ctx.last_name].filter(Boolean).join(' ').toLowerCase();
      return this.contacts.filter((c) => {
        if (c.source !== 'contact') return false;
        if (npi && c.npi === npi) return true;
        if (name && this.displayName(c).toLowerCase().includes(name)) return true;
        return false;
      });
    },

    contextProviderName() {
      const ctx = this.waterfallResults?.context;
      if (!ctx) return '';
      return [ctx.first_name, ctx.last_name].filter(Boolean).join(' ');
    },
  },

  async created() {
    if (this.waterfallContext) {
      await this.runWaterfall();
      this.loadContacts();
    } else {
      await this.loadContacts();
      if (this.initialNpi.trim()) this.showDirectorySearch = true;
    }
  },

  mounted() {
    this.$refs.searchInput?.focus();
  },

  methods: {
    async loadContacts() {
      this.loadingContacts = true;
      try {
        this.contacts = await messagingApi.listContacts();
        this.contactsUnavailable = false;
      } catch {
        this.contactsUnavailable = true;
        this.contacts = [];
      } finally {
        this.loadingContacts = false;
      }
    },

    async runWaterfall() {
      this.waterfallLoading = true;
      try {
        this.waterfallResults = await messagingApi.waterfallSearch(this.waterfallContext);
      } catch {
        this.waterfallResults = null;
      } finally {
        this.waterfallLoading = false;
      }
    },

    participantDisplayName,

    displayName(entry) {
      return participantDisplayName(entry) || entry.direct_address || 'Unknown';
    },

    selectRecipient(entry) {
      this.$emit('select', {
        direct_address: entry.direct_address,
        display_name: participantDisplayName(entry),
      });
    },

    selectWaterfallResult(result) {
      messagingApi.createContact({
        direct_address: result.direct_address,
        first_name: result.first_name ?? '',
        last_name: result.last_name ?? '',
        organization: result.organization ?? '',
        npi: result.npi ?? '',
        phone: result.phone ?? '',
        fax: result.fax ?? '',
        address1: result.address1 ?? '',
        address2: result.address2 ?? '',
        city: result.city ?? '',
        state: result.state ?? '',
        postal: result.postal ?? '',
      }).catch(() => {});
      this.$emit('select', {
        direct_address: result.direct_address,
        display_name: participantDisplayName(result),
      });
    },

    getResultTier(result) {
      if (!this.waterfallResults?.tiers) return '';
      for (const tier of this.waterfallResults.tiers) {
        if (tier.results.some((r) => r.direct_address === result.direct_address)) return tier.tier;
      }
      return '';
    },

    async selectFromDirectory(result) {
      this.showDirectorySearch = false;
      try {
        await messagingApi.createContact({
          direct_address: result.direct_address,
          first_name: result.first_name ?? '',
          last_name: result.last_name ?? '',
          organization: result.organization ?? '',
          npi: result.npi ?? '',
          phone: result.phone ?? '',
          fax: result.fax ?? '',
          address1: result.address1 ?? '',
          address2: result.address2 ?? '',
          city: result.city ?? '',
          state: result.state ?? '',
          postal: result.postal ?? '',
        });
        await this.loadContacts();
      } catch (e) {
        console.warn('[RecipientPicker] failed to save directory contact:', e?.message);
      }
      this.$emit('select', {
        direct_address: result.direct_address,
        display_name: participantDisplayName(result),
      });
    },

    tierBadgeClass(tier) {
      const classes = {
        npi: 'bg-emerald-100 text-emerald-700 border-emerald-200',
        name_state: 'bg-blue-100 text-blue-700 border-blue-200',
        org_state: 'bg-amber-100 text-amber-700 border-amber-200',
      };
      return classes[tier] || 'bg-gray-100 text-gray-700 border-gray-200';
    },

    flagClass(type) {
      const classes = {
        ORG_LEVEL: 'bg-blue-50 text-blue-600 border-blue-200',
        MULTI_PRACTICE: 'bg-amber-50 text-amber-600 border-amber-200',
        NO_NPI: 'bg-gray-50 text-gray-500 border-gray-200',
      };
      return classes[type] || 'bg-gray-50 text-gray-500 border-gray-200';
    },

    flagShortLabel(type) {
      const labels = { ORG_LEVEL: 'Office-wide address', MULTI_PRACTICE: 'Multiple practices', NO_NPI: 'No NPI' };
      return labels[type] || type;
    },
  },
};
</script>
